/**
 * Masked ISO-date entry helpers (T-06 / T-07).
 *
 * The contract editor's `DateField` is a plain text input: you type 8
 * digits in year→month→day order and the dashes auto-insert
 * ("20260519" → "2026-05-19"), independent of OS locale. As each segment
 * completes it is range-checked and clamped to a real calendar value, so
 * an impossible month/day can't be committed:
 *
 *   "1998-44"    → month clamps to 12   → "1998-12"
 *   "1998-12-33" → day clamps to Dec's 31 → "1998-12-31"
 *   "2024-02-30" → day clamps to Feb 2024's 29 (leap) → "2024-02-29"
 *
 * Leap years and month lengths come from `dayjs` rather than hand-rolled
 * arithmetic. Storage stays an ISO `YYYY-MM-DD` string.
 */
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat.js';
dayjs.extend(customParseFormat);
export const DATE_MASK_PLACEHOLDER = 'ÅÅÅÅ-MM-DD';
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function pad2(n) {
    return String(n).padStart(2, '0');
}
function clampMonth(mm) {
    return pad2(Math.min(Math.max(Number(mm), 1), 12));
}
function clampDay(yyyy, mm, dd) {
    const anchor = dayjs(`${yyyy}-${mm}-01`, 'YYYY-MM-DD', true);
    const maxDay = anchor.isValid() ? anchor.daysInMonth() : 31;
    return pad2(Math.min(Math.max(Number(dd), 1), maxDay));
}
/**
 * Format raw input into a masked ISO string, clamping any segment that is
 * fully typed (2 digits). Partial segments (an in-progress month/day) are
 * left untouched so the field doesn't fight the cursor mid-keystroke.
 */
export function maskDateInput(raw) {
    const digits = raw.replace(/\D/g, '').slice(0, 8);
    const year = digits.slice(0, 4);
    let month = digits.slice(4, 6);
    let day = digits.slice(6, 8);
    if (month.length === 2)
        month = clampMonth(month);
    if (day.length === 2)
        day = clampDay(year, month, day);
    let out = year;
    if (digits.length > 4)
        out += `-${month}`;
    if (digits.length > 6)
        out += `-${day}`;
    return out;
}
/** True only for a complete, real calendar date in `YYYY-MM-DD` form. */
export function isCompleteIsoDate(value) {
    return ISO_DATE_RE.test(value) && dayjs(value, 'YYYY-MM-DD', true).isValid();
}
/** Narrow guard for feeding a value to the native `<input type="date">`. */
export function isIsoDateString(value) {
    return ISO_DATE_RE.test(value);
}
//# sourceMappingURL=date-mask.js.map